import React, { useState, useEffect } from 'react';
import SeatingPlan from './SeatingPlan';
import TableLayout from './TableLayout';
import Planning from './Planning';
import './App.css';

function Admin() {
  const [rsvps, setRsvps] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('guests');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editData, setEditData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [focusTableId, setFocusTableId] = useState(null);
  const [newGuest, setNewGuest] = useState({
    firstName: '', lastName: '', attending: 'yes', guests: 1, guestNames: '', kids: 0, contact: ''
  });

  const fetchRsvps = async () => {
    try {
      const response = await fetch('/api/admin/rsvps', { method: 'GET' });
      if (response.status === 200) {
        const data = await response.json();
        setRsvps(data);
        setError('');
      } else {
        setError('Invalid Credentials');
      }
    } catch (err) {
      setError('Server Error');
    }
  };

  useEffect(() => {
    fetchRsvps();
  }, []);

  const totalAdults = rsvps.reduce((acc, curr) => {
    return curr.attending === 'yes' ? acc + parseInt(curr.guests || 0) : acc;
  }, 0);

  const totalKids = rsvps.reduce((acc, curr) => {
    return curr.attending === 'yes' ? acc + parseInt(curr.kids || 0) : acc;
  }, 0);

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditData({ ...rsvps[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditData(null);
  };

  const saveEdit = async () => {
    const res = await fetch(`/api/admin/rsvps/${editingIndex}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData),
    });
    if (res.ok) {
      await fetchRsvps();
      setEditingIndex(null);
      setEditData(null);
    }
  };

  const deleteGuest = async (index) => {
    if (!window.confirm(`Remove ${rsvps[index].firstName} ${rsvps[index].lastName}?`)) return;
    const res = await fetch(`/api/admin/rsvps/${index}`, { method: 'DELETE' });
    if (res.ok) await fetchRsvps();
  };

  const addGuest = async () => {
    if (!newGuest.firstName.trim() || !newGuest.lastName.trim()) return;
    const res = await fetch('/api/admin/rsvps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newGuest),
    });
    if (res.ok) {
      await fetchRsvps();
      setNewGuest({ firstName: '', lastName: '', attending: 'yes', guests: 1, guestNames: '', kids: 0, contact: '' });
      setAdding(false);
    }
  };

  const toggleConfirmed = async (index) => {
    const rsvp = rsvps[index];
    const updated = { ...rsvp, confirmed: !rsvp.confirmed };
    const res = await fetch(`/api/admin/rsvps/${index}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    });
    if (res.ok) await fetchRsvps();
  };

  const editField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
    <div className={tab === 'layout' ? 'admin-container admin-container-layout' : 'admin-container'}>
      <div className="admin-nav">
        <button className={tab === 'guests' ? 'active' : ''} onClick={() => setTab('guests')}>
          Guest List
        </button>
        <button className={tab === 'seating' ? 'active' : ''} onClick={() => setTab('seating')}>
          Seating Plan
        </button>
        <button className={tab === 'layout' ? 'active' : ''} onClick={() => setTab('layout')}>
          Table Layout
        </button>
        <button className={tab === 'planning' ? 'active' : ''} onClick={() => setTab('planning')}>
          Planning
        </button>
      </div>

      {tab === 'guests' && (
        <>
          <header style={{ textAlign: 'center', padding: '20px' }}>
            <h1>Wedding Guest List</h1>
            <div className="seating-stats">
              <div className="total-badge">
                Adults: <strong>{totalAdults}</strong> | Kids: <strong>{totalKids}</strong> | Total: <strong>{totalAdults + totalKids}</strong>
              </div>
            </div>
            <button
              className="add-guest-btn"
              onClick={() => setAdding(!adding)}
            >
              {adding ? 'Cancel' : '+ Add Attendee'}
            </button>
          </header>

          {adding && (
            <div className="add-guest-form">
              <div className="form-row">
                <div>
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newGuest.firstName}
                    onChange={e => setNewGuest({ ...newGuest, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newGuest.lastName}
                    onChange={e => setNewGuest({ ...newGuest, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label>Status</label>
                  <select
                    value={newGuest.attending}
                    onChange={e => setNewGuest({ ...newGuest, attending: e.target.value })}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label>Attendees</label>
                  <input
                    type="number"
                    min="1"
                    value={newGuest.guests}
                    onChange={e => setNewGuest({ ...newGuest, guests: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label>Kids</label>
                  <input
                    type="number"
                    min="0"
                    value={newGuest.kids}
                    onChange={e => setNewGuest({ ...newGuest, kids: e.target.value })}
                  />
                </div>
              </div>
              <label>Guest Names</label>
              <textarea
                rows="2"
                value={newGuest.guestNames}
                onChange={e => setNewGuest({ ...newGuest, guestNames: e.target.value })}
              />
              <label>Contact</label>
              <input
                type="text"
                value={newGuest.contact}
                onChange={e => setNewGuest({ ...newGuest, contact: e.target.value })}
              />
              <button onClick={addGuest}>Save Attendee</button>
            </div>
          )}

          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th className="col-c">C</th>
                  <th className="col-num">#</th>
                  <th>Name</th>
                  <th>Surname</th>
                  <th className="col-status">Status</th>
                  <th className="col-num">N</th>
                  <th>Guests</th>
                  <th className="col-num">K</th>
                  <th>Contact</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rsvps.map((guest, index) => {
                  const isEditing = editingIndex === index;

                  if (isEditing) {
                    return (
                      <tr key={index} className="editing-row">
                        <td className="col-c">
                          <input type="checkbox" checked={!!editData.confirmed} onChange={e => editField('confirmed', e.target.checked)} />
                        </td>
                        <td>{index + 1}</td>
                        <td>
                          <input
                            className="inline-edit"
                            value={editData.firstName}
                            onChange={e => editField('firstName', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="inline-edit"
                            value={editData.lastName}
                            onChange={e => editField('lastName', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="inline-edit"
                            value={editData.attending}
                            onChange={e => editField('attending', e.target.value)}
                          >
                            <option value="yes">YES</option>
                            <option value="no">NO</option>
                          </select>
                        </td>
                        <td>
                          <input
                            className="inline-edit"
                            type="number"
                            min="1"
                            value={editData.guests || 1}
                            onChange={e => editField('guests', e.target.value)}
                          />
                        </td>
                        <td>
                          <textarea
                            className="inline-edit"
                            rows="2"
                            value={editData.guestNames || ''}
                            onChange={e => editField('guestNames', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="inline-edit"
                            type="number"
                            min="0"
                            value={editData.kids || 0}
                            onChange={e => editField('kids', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="inline-edit"
                            value={editData.contact || ''}
                            onChange={e => editField('contact', e.target.value)}
                          />
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn save-btn" onClick={saveEdit} title="Save">✓</button>
                          <button className="action-btn cancel-btn" onClick={cancelEdit} title="Cancel">✕</button>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={index} className={guest.attending === 'no' ? 'declined-row' : ''}>
                      <td className="col-c">
                        <input type="checkbox" checked={!!guest.confirmed} onChange={() => toggleConfirmed(index)} />
                      </td>
                      <td>{index + 1}</td>
                      <td>{guest.firstName}</td>
                      <td>{guest.lastName}</td>
                      <td style={{ fontWeight: 'bold', color: guest.attending === 'yes' ? 'green' : 'red' }}>
                        {guest.attending.toUpperCase()}
                      </td>
                      <td>{guest.attending === 'yes' ? guest.guests : '-'}</td>
                      <td>{guest.guestNames || '-'}</td>
                      <td>{guest.kids || 0}</td>
                      <td>{guest.contact || '-'}</td>
                      <td className="actions-cell">
                        <button className="action-btn edit-btn" onClick={() => startEdit(index)} title="Edit">✎</button>
                        <button className="action-btn delete-btn" onClick={() => deleteGuest(index)} title="Delete">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'seating' && <SeatingPlan focusTableId={focusTableId} onFocusHandled={() => setFocusTableId(null)} />}
      {tab === 'planning' && <Planning />}
    </div>
    {tab === 'layout' && <TableLayout onTableDoubleClick={(tableId) => {
      setFocusTableId(tableId);
      setTab('seating');
    }} />}
    </>
  );
}

export default Admin;
